import { useState, useEffect } from "react";
import { useAuth } from "./useAuth";
import { preferenceService } from "../services/preferenceService";
import { toast } from "react-toastify";

export const usePreferences = () => {
  const { user } = useAuth();

  // State for user data
  const [userEmails, setUserEmails] = useState({
    work_email: "",
  });

  // State for form inputs
  const [emailForm, setEmailForm] = useState({
    newEmail: "",
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    retypedPassword: "",
  });

  const [resetPasswordForm, setResetPasswordForm] = useState({
    workEmail: "",
  });

  // Password validation states
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: "",
    newPassword: "",
    retypedPassword: "",
    match: "",
  });

  // Loading states
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Success/error states
  const [emailUpdateSuccess, setEmailUpdateSuccess] = useState(false);
  const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);

  // Handle email form input changes
  const handleEmailInputChange = (e) => {
    setEmailForm((prev) => ({
      ...prev,
      newEmail: e.target.value,
    }));
  };

  // Handle password form input changes
  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;

    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setPasswordErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    if (name === "newPassword" || name === "retypedPassword") {
      validatePasswordMatch();
    }
  };

  // Validate password match in real-time
  const validatePasswordMatch = () => {
    const { newPassword, retypedPassword } = passwordForm;

    if (retypedPassword && newPassword !== retypedPassword) {
      setPasswordErrors((prev) => ({
        ...prev,
        match: "Passwords do not match",
      }));
    } else {
      setPasswordErrors((prev) => ({
        ...prev,
        match: "",
      }));
    }
  };

  const handleResetPasswordInputChange = (e) => {
    setResetPasswordForm((prev) => ({
      ...prev,
      workEmail: e.target.value,
    }));
  };

  const handleUpdateEmail = async () => {
    if (!emailForm.newEmail.trim()) {
      toast.error("Please enter a new email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailForm.newEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (emailForm.newEmail === userEmails.work_email) {
      toast.error("New email must be different from current email");
      return;
    }

    setIsUpdatingEmail(true);
    try {
      await preferenceService.updateUserEmail(
        userEmails.work_email,
        emailForm.newEmail
      );

      setEmailUpdateSuccess(true);
      setEmailForm((prev) => ({ ...prev, newEmail: "" }));
      setTimeout(() => setEmailUpdateSuccess(false), 5000);
    } catch (error) {
      console.error("Failed to update email:", error);
      toast.error(error.message);
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleUpdatePassword = async () => {
    const { currentPassword, newPassword, retypedPassword } = passwordForm;

    setPasswordErrors({
      currentPassword: "",
      newPassword: "",
      retypedPassword: "",
      match: "",
    });

    let hasErrors = false;
    const errors = {};

    if (!currentPassword.trim()) {
      errors.currentPassword = "Current password is required";
      hasErrors = true;
    }

    if (!newPassword.trim()) {
      errors.newPassword = "New password is required";
      hasErrors = true;
    }

    if (!retypedPassword.trim()) {
      errors.retypedPassword = "Please retype your new password";
      hasErrors = true;
    }

    if (newPassword !== retypedPassword) {
      errors.match = "Passwords do not match";
      hasErrors = true;
    }

    if (newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters long";
      hasErrors = true;
    }

    if (newPassword && !/(?=.*[a-z])/.test(newPassword)) {
      errors.newPassword =
        "Password must contain at least one lowercase letter";
      hasErrors = true;
    }

    if (newPassword && !/(?=.*[A-Z])/.test(newPassword)) {
      errors.newPassword =
        "Password must contain at least one uppercase letter";
      hasErrors = true;
    }

    if (newPassword && !/(?=.*\d)/.test(newPassword)) {
      errors.newPassword = "Password must contain at least one number";
      hasErrors = true;
    }

    if (newPassword && !/(?=.*[!@#$%^&*])/.test(newPassword)) {
      errors.newPassword =
        "Password must contain at least one special character (!@#$%^&*)";
      hasErrors = true;
    }

    if (hasErrors) {
      setPasswordErrors(errors);
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const response = await preferenceService.updatePassword(
        currentPassword,
        newPassword,
        retypedPassword
      );

      setPasswordUpdateSuccess(true);
      toast.success(response.message || "Password updated successfully!");

      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        retypedPassword: "",
      });

      setPasswordErrors({
        currentPassword: "",
        newPassword: "",
        retypedPassword: "",
        match: "",
      });

      setTimeout(() => setPasswordUpdateSuccess(false), 5000);
    } catch (error) {
      console.error("Failed to update password:", error);
      toast.error(error.message);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordForm.workEmail.trim()) {
      toast.error("Please enter your work email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetPasswordForm.workEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsResettingPassword(true);
    try {
      const response = await preferenceService.resetPassword(
        resetPasswordForm.workEmail
      );

      setResetPasswordSuccess(true);
      toast.success(
        response.message ||
          "Password reset request sent. Check your work email to reset the password."
      );

      setResetPasswordForm((prev) => ({ ...prev, workEmail: "" }));

      setTimeout(() => setResetPasswordSuccess(false), 5000);
    } catch (error) {
      console.error("Failed to send password reset:", error);
      toast.error(error.message);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const checkEmailVerificationToken = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");

    if (token) {
      try {
        await preferenceService.verifyUpdatedEmail(token);
        toast.success("Email verified successfully!");

        // Clean up URL
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      } catch (error) {
        console.error("Failed to verify email:", error);
        toast.error(error.message);
      }
    }
  };

  useEffect(() => {
    checkEmailVerificationToken();
  }, []);

  return {
    // State
    userEmails,
    emailForm,
    passwordForm,
    resetPasswordForm,
    isLoadingEmails,
    isUpdatingEmail,
    isUpdatingPassword,
    isResettingPassword,
    emailUpdateSuccess,
    passwordUpdateSuccess,
    resetPasswordSuccess,
    passwordErrors,

    // Actions
    handleEmailInputChange,
    handlePasswordInputChange,
    handleResetPasswordInputChange,
    handleUpdateEmail,
    handleUpdatePassword,
    handleResetPassword,
  };
};